import { Id } from '@silevis/reactgrid'
import { ScenarioElectricityPriceData } from 'api/elplan/elplan.api'
import Datetime from 'utils/datetime/datetime'

import { ElectricityPriceScenario, RowModel } from './types'

export function rowModelsToPriceData(rowModels: RowModel[]): ScenarioElectricityPriceData[] {
  const priceDataByScenario: { [key: number]: ScenarioElectricityPriceData } = {}
  for (const scenario of rowModels) {
    for (const key in scenario.scenarios) {
      const scenarioId = parseScenarioId(key)
      const value = scenario.scenarios[key]
      priceDataByScenario[scenarioId] = priceDataByScenario[scenarioId] || {
        id: scenarioId,
        values: [],
      }
      priceDataByScenario[scenarioId].values.push({
        time: scenario.time,
        value: value ?? NaN,
      })
    }
  }
  return Object.values(priceDataByScenario)
}

export function generateDefaultPriceValues(
  scenarios: ElectricityPriceScenario[],
  startTime: ISODateTime,
  endTime: ISODateTime
): ScenarioElectricityPriceData[] {
  const hourlyTimestamps = Datetime.getHoursBetween(startTime, endTime)

  return scenarios.map((scenario) => ({
    id: scenario.id,
    values: hourlyTimestamps.map((time) => ({
      time,
      value: NaN,
    })),
  }))
}

export function priceDataToRowModels(
  timeArray: ISODateTime[],
  electricityPriceData: ScenarioElectricityPriceData[],
  forecast: ScenarioElectricityPriceData[],
  scenarios: ElectricityPriceScenario[]
): RowModel[] {
  return timeArray.map((time) => {
    const scenarioValues = scenarios.reduce(
      (acc, scenario) => {
        const scenarioKey = getScenarioRowKey(scenario)
        const dataPoint = electricityPriceData?.find((data) => data.id === scenario.id)
        const valueForTime = dataPoint?.values.find((v) => Datetime.isSameHour(v.time, time))

        acc[scenarioKey] = valueForTime?.value

        return acc
      },
      {} as { [key: string]: number | undefined }
    )

    const forecastValue = forecast?.[0]?.values?.find((v) => Datetime.isSameHour(v.time, time))?.value ?? NaN

    return {
      time: time,
      scenarios: scenarioValues,
      forecast: Math.round(forecastValue * 10) / 10,
    }
  })
}

export function getScenarioPropertyMap(
  scenarios: ElectricityPriceScenario[]
): Array<{ scenarioId: number | string; propertyName: string }> {
  return scenarios.map((scenario) => {
    return {
      scenarioId: scenario.id,
      propertyName: scenario.uiconfig_override_alias.electricity_price_override_prop?.toString() ?? '',
    }
  })
}

export function getUpdatedRowModels(
  rowModels: RowModel[],
  columnToEdit: Id,
  newValues: ScenarioElectricityPriceData
): RowModel[] {
  return rowModels.map((rowModel) => {
    const yesterdayTime = Datetime.getYesterdayFromTime(rowModel.time)

    const newValue = newValues.values.find((v) => Datetime.isSameHour(v.time, yesterdayTime))?.value

    return {
      ...rowModel,
      scenarios: {
        ...rowModel.scenarios,
        [columnToEdit]: newValue,
      },
    }
  })
}

function getScenarioRowKey(scenario: ElectricityPriceScenario): string {
  return `scenario_${scenario.id}`
}

function parseScenarioId(scenarioRowKey: string): number {
  return Number(scenarioRowKey.split('_')[1])
}
